import { IoInformationCircleOutline } from 'react-icons/io5';
import { ActionSymbol } from '../../components/ui/ActionSymbol';
import { ActionButton, ActionIconButton } from '../../components/ui/ActionButton';
import React, { useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAnnualPlanTranslation } from '../../hooks/useAnnualPlanTranslation';

const steps = ['initialInfo', 'races', 'ctlTarget', 'trainingModel', 'tssReview', 'confirmBuild'];
const sources = [
  ['The comprehensive guide to annual training plans', 'https://www.trainingpeaks.com/learn/articles/the-comprehensive-guide-to-creating-an-annual-training-plan/'],
  ['Annual Training Plan methodologies', 'https://help.trainingpeaks.com/hc/en-us/articles/224662768-Annual-Training-Plan-ATP-Methodologies'],
  ['How to create your annual cycling training plan', 'https://www.trainingpeaks.com/blog/create-annual-cycling-training-plan/'],
];

export function AnnualPlanGuide({ step }: { step: number }) {
  const { t, i18n } = useAnnualPlanTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const currentStep = Math.max(0, Math.min(steps.length - 1, step));
  const stepKey = steps[currentStep];
  const seenKey = `vo2-annual-onboarding-v1-${stepKey}`;
  const dismiss = () => dialog.current?.close();
  return <>
    <ActionButton
      type="button"
      onClick={() => dialog.current?.showModal()}
      aria-label={t('annualPlan.guide.title')}
      wrap
      icon={<IoInformationCircleOutline aria-hidden="true" />}
    >{t('annualPlan.guide.button')}</ActionButton>
    {createPortal(<dialog ref={dialog} aria-labelledby={titleId} dir={i18n.dir()}
      onClose={() => { try { sessionStorage.setItem(seenKey, '1'); } catch { /* Optional preference. */ } }}
      onClick={event => { if (event.target === event.currentTarget) dismiss(); }}
      style={{ width: 'min(640px, calc(100vw - 24px))', maxHeight: '85dvh', margin: 'auto', boxSizing: 'border-box', padding: 0, border: '1px solid var(--surface-2)', borderRadius: 20, background: 'var(--surface-0)', color: 'var(--fg-1)', boxShadow: '0 24px 80px #0008' }}>
      <div style={{ padding: 22 }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h2 id={titleId} style={{ fontSize: 19, margin: 0 }}>{t('annualPlan.guide.title')}</h2>
          <ActionIconButton
            type="button"
            autoFocus
            onClick={() => dialog.current?.close()}
            label={t('common.close')}
            icon={<ActionSymbol name="close" />}
          />
        </header>
        <p style={{ color: 'var(--fg-2)', fontSize: 12 }}>{t('annualPlan.wizard.stepOf', { current: currentStep + 1, total: steps.length })}</p>
        <nav aria-label={t('annualPlan.guide.title')} style={{ display: 'flex', gap: 6, marginBlock: 16 }}>
          {steps.map((key, index) => <span key={key} aria-current={index === currentStep ? 'step' : undefined}
            style={{ height: 4, flex: 1, borderRadius: 4, background: index === currentStep ? 'var(--accent, #df1765)' : 'var(--surface-2)' }} />)}
        </nav>
        <section key={stepKey}>
          <h3 style={{ fontSize: 18, margin: '0 0 12px' }}>{t(`annualPlan.wizard.steps.${stepKey}`)}</h3>
          <p style={{ margin: 0, lineHeight: 2, fontSize: 14, color: 'var(--fg-2)' }}>{t(`annualPlan.guide.${stepKey}`)}</p>
        </section>
        <ActionButton
          type="button"
          onClick={dismiss}
          wrap
          variant="primary"
          style={{ width: "100%", marginBlock: 20 }}
        >{t('annualPlan.guide.startStep')}</ActionButton>
        <details>
          <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--fg-2)' }}>{t('annualPlan.guide.sources')}</summary>

        <ul style={{ paddingInlineStart: 20, fontSize: 12, lineHeight: 1.8 }}>{sources.map(([title, url]) => <li key={url}><a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--fg-1)' }}>{title}</a></li>)}</ul>
        </details>
      </div>
    </dialog>, document.body)}
  </>;
}
